import uiConfig from 'src/lib/ui/config';
import { Script } from 'src/replicant/conversation/conservation.ruleset';
import { Widen } from 'src/replicant/utils/types';
import StateObserver from 'src/StateObserver';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import sounds from 'src/lib/sounds';
import PopupSpeechBubbleBase, {
  SpeechBubbleCloseHandler,
} from './PopupSpeechBubbleBase';
import { animate, View } from '@play-co/timestep-core/ui';
import { GCInstant as platform } from 'src/lib/gcinstant';
import i18n from 'src/lib/i18n/i18n';
import {
  getPlayerIdFromConversation,
  getScriptText,
} from 'src/game/conversation/conversation.utils';
import { getScreenTop } from '../../../lib/utils';
import { WantToAddFriendResult } from 'src/game/conversation/PetConversationController';

export default class PopupWantToAddFriend extends PopupSpeechBubbleBase<WantToAddFriendResult> {
  private buttons: ButtonScaleViewWithText[] = [];

  constructor(opts: {
    superview: View;
    close: SpeechBubbleCloseHandler<WantToAddFriendResult>;
  }) {
    super({ ...opts, $subFeature: 'want-to-add-friend' });
  }

  public init({ id, move_to }: Pick<Widen<Script>, 'id' | 'move_to'>) {
    const { user, ui } = StateObserver.getState();

    super.init({
      id,
      move_to,
      message: getScriptText(
        user,
        id,
        ui.locale,
        getPlayerIdFromConversation(id) !== platform.playerID,
      ),
    });

    const baseOptionY = 650 - getScreenTop();
    this.buttons = [
      {
        label: () => i18n('conversation.wantToAddFriend.addFriend'),
        result: 'addFriend' as const,
        config: uiConfig.buttons.secondary,
      },
      {
        label: () => i18n('conversation.wantToAddFriend.maybeLater'),
        result: 'maybeLater' as const,
        config: uiConfig.buttons.cancel,
      },
    ].map((option, i, { length }) => {
      const button = new ButtonScaleViewWithText({
        ...option.config,
        width: 400,
        height: 90,
        centerOnOrigin: true,
        x: this.box.style.width / 2,
        y: baseOptionY - 102 * (length - i - 1),
        font: 'Body',
        fontSize: 32,
        localeText: option.label,
        onClick: async () => {
          sounds.sfx.play('ok');
          this.opts.close(option.result);
        },
        tracking: {
          feature: 'conversation-answer',
          $subFeature: `want-to-add-friend-${option.result}`,
        },
        visible: false,
        opacity: 0,
        scaleX: 0.9,
        scaleY: 0.9,
      });
      button.setDisabled(true);
      this.box.addSubview(button);

      return button;
    });
  }

  public async fadeIn() {
    await super.fadeIn();

    this.currentAnimation?.then(() => {
      this.tapIcon.hide();
      this.buttons.forEach((button, i) => {
        button.updateOpts({ visible: true });
        animate(button)
          .wait(i * 50)
          .then({ opacity: 1, scaleX: 1, scaleY: 1 }, 100)
          .then(() => button.setDisabled(false));
      });
    });
  }
}
